const TOKEN_KEY = "passport_portal_token";
const USER_KEY = "passport_portal_user";

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) || "";
}

export function setSession(token: string, user: unknown) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/** Dead JWT in localStorage — send staff back to sign-in instead of showing Invalid token. */
export function forceLogin() {
  clearSession();
  if (!window.location.pathname.startsWith("/portal/login")) {
    window.location.assign("/portal/login");
  }
}

function isAuthApi(path: string): boolean {
  return path.startsWith("/api/auth/");
}

function shouldForceLogin(status: number, detail: unknown): boolean {
  if (status !== 401) return false;
  if (typeof detail !== "string") return true;
  return (
    detail === "Invalid token"
    || detail === "Not authenticated"
    || detail === "User inactive"
  );
}

export function getStoredUser<T>(): T | null {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null") as T | null;
  } catch {
    return null;
  }
}

function friendlyError(status: number, statusText: string, detail: unknown): string {
  if (status === 502 || status === 503 || status === 504) {
    return "Server temporarily unavailable — try Refresh in a moment.";
  }
  if (typeof detail === "string") {
    const trimmed = detail.trim();
    if (trimmed.startsWith("<") || trimmed.toLowerCase().includes("<html")) {
      return `Server error (${status}). Try Refresh.`;
    }
    return trimmed.slice(0, 240);
  }
  if (detail != null) {
    try {
      return JSON.stringify(detail).slice(0, 240);
    } catch {
      /* ignore */
    }
  }
  return statusText || `Request failed (${status})`;
}

export async function api<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers = new Headers(opts.headers || {});
  if (!headers.has("Content-Type") && opts.body) {
    headers.set("Content-Type", "application/json");
  }
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(path, { ...opts, headers });
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { detail: text };
  }
  if (!res.ok) {
    const detail = (data as { detail?: unknown; message?: string } | null)?.detail
      ?? (data as { message?: string } | null)?.message
      ?? null;
    if (!isAuthApi(path) && shouldForceLogin(res.status, detail)) {
      forceLogin();
    }
    throw new Error(friendlyError(res.status, res.statusText, detail));
  }
  return data as T;
}

export async function fetchBlob(path: string): Promise<Blob> {
  const headers: HeadersInit = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(path, { headers });
  if (!res.ok) {
    if (res.status === 401) forceLogin();
    throw new Error(await res.text() || res.statusText);
  }
  return res.blob();
}
